import { CaretLeft } from "@phosphor-icons/react";
import { useEDCFlow } from "@smvtech/x-flux";
import useConfetti from "pages/EDCFlow/hooks/useConfetti";
import { useEffect, useRef } from "react";
import { Flag } from "x-wings";
import { Flags } from "x-wings/Components/Flag/types";

type Props = {
	onPrev: () => void;
	travellerName?: string;
};

const FinalSuccessScreen = ({ onPrev, travellerName }: Props) => {
	const { order } = useEDCFlow();

	const hasFiredRef = useRef(false);

	const confetti = useConfetti({
		particle_size: 3,
		particle_count: 500,
		destroy_target: false,
		explosion_power: 50,
		fade: true,
		gravity: 30
	});

	useEffect(() => {
		if (hasFiredRef.current) return;

		const timeout = setTimeout(() => {
			confetti.pop(window.innerWidth / 2, window.innerHeight / 2);
			hasFiredRef.current = true;
		}, 250);

		return () => clearTimeout(timeout);
	}, []);

	return (
		<div className="h-dvh bg-white flex flex-col overflow-hidden">
			<div className="bg-white z-20 border-b border-gray-100 shrink-0">
				<div className="flex items-center justify-between px-4 py-[13px]">
					<button onClick={onPrev} className="justify-start">
						<CaretLeft size={20} />
					</button>
					<div className="flex items-center gap-2">
						<Flag code={order?.country?.symbol as Flags} size="m" hasBorder hasBorderRadius hasDropShadow />
						<span className="text-sm font-medium text-black">
							{travellerName ? `${travellerName}'s` : "Your"} Information
						</span>
					</div>
					<div className="w-5" />
				</div>
			</div>

			<div className="flex-1 overflow-y-auto bg-white flex flex-col items-center px-6 py-10">
				<img
					src="https://stampmyvisa-public.s3.ap-south-1.amazonaws.com/biz-share-questionnaire/airplane-success.png"
					alt="Success"
					className="w-[293px] h-[293px] mb-8"
				/>
				<h1 className="text-2xl font-bold text-center text-gray-900 mb-2">You have successfully completed!</h1>

				<p className="text-sm text-gray-500 text-center max-w-xs mb-8">
					We are excited to apply your documents to the embassy to get you to{" "}
					<span className="text-indigo-600 font-bold">{order?.country?.name}!</span>
				</p>

				<div className="w-full bg-gray-50 rounded-2xl p-5 space-y-4 shadow-sm">
					<h2 className="text-base font-bold text-indigo-600">Next Steps</h2>

					<div className="space-y-5 text-gray-700 bg-gray-50">
						<div className="flex gap-3 items-center">
							<div className="w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm shrink-0">
								1
							</div>
							<p className="text-base leading-snug pt-1">
								Your documents will be carefully reviewed by our team.
							</p>
						</div>

						<div className="flex gap-3 items-center">
							<div className="w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm shrink-0">
								2
							</div>
							<p className="text-base leading-snug pt-1">
								Once approved, we'll forward your application to the embassy.
							</p>
						</div>

						<div className="flex gap-3 items-center">
							<div className="w-6 h-6 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm shrink-0">
								3
							</div>
							<p className="text-base leading-snug pt-1">
								You'll be informed of your visa status within 10 days of submission.
							</p>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default FinalSuccessScreen;

